import dayjs from "dayjs";

export type BladeInfo = {
  serial_number?: string | null;
  model?: string | null;
  manufacturer?: string | null;
};

type AssigneeInfo = {
  username: string;
  first_name?: string;
  last_name?: string;
};

export type WOClipboardContext = {
  locationName?: string | null;
  turbineSerialNumber?: string | null;
  blades: BladeInfo[];
  caseNumber?: string | null;
  deadline?: string | null;
  ticketNumber?: number | null;
  woAssignees: AssigneeInfo[];
};

type SessionClipboardParams = WOClipboardContext & {
  startedAt?: string | null;
  endedAt?: string | null;
  taskCheckin?: string | null;
  damCheckin?: string | null;
  dfCheckin?: string | null;
  weatherCheckin?: string | null;
  progressCheckin?: string | null;
  carCheckin?: string | null;
  taskCheckout?: string | null;
  damCheckout?: string | null;
  dfCheckout?: string | null;
  weatherCheckout?: string | null;
  progressCheckout?: string | null;
  turbineStoppedCheckout?: string | null;
  carCheckout?: string | null;
  // When provided (field app), the report shows the blades selected at check-in
  // instead of the whole turbine's blade list. Left undefined by callers that want
  // the list.
  selectedBlades?: BladeInfo[] | null;
  phase?: "checkin" | "checkout" | "full";
};

// One phase's own values; the work-order context around them is the same in both.
// The progress field is forward-looking at check-in and retrospective at check-out,
// so each phase carries the label its own form uses.
type PhaseValues = {
  dateTime: string;
  progressLabel: string;
  task?: string | null;
  dam?: string | null;
  df?: string | null;
  weather?: string | null;
  progress?: string | null;
  // Reported only by the block that closes the session, so the check-in block
  // leaves both undefined and their lines are left out of it.
  turbineStopped?: string | null;
  estimatedCompletionDate?: string | null;
  car?: string | null;
};

export function formatBladeParts(blade: BladeInfo): string {
  return [f(blade.serial_number), f(blade.model), f(blade.manufacturer)].join(" / ");
}

// Multiple tasks can reference the same blade; the report only renders the
// serial/model/manufacturer triple, so dedup on that triple keeps each distinct
// blade to a single line while preserving first-appearance order.
function pushNumberedBladeLines(lines: string[], bladeList: BladeInfo[]): void {
  const seenBladeKeys = new Set<string>();
  const distinctBlades = bladeList.filter((blade) => {
    const bladeKey = [f(blade.serial_number), f(blade.model), f(blade.manufacturer)].join(
      "|"
    );
    if (seenBladeKeys.has(bladeKey)) return false;
    seenBladeKeys.add(bladeKey);
    return true;
  });
  distinctBlades.forEach((blade, index) => {
    lines.push(`Blade ${index + 1}: ${formatBladeParts(blade)}`);
  });
}

function f(value: string | null | undefined): string {
  return value?.trim() || "—";
}

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  return dayjs(value).format("YYYY-MM-DD HH:mm");
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  return dayjs(value).format("YYYY-MM-DD");
}

function fmtTurbineStatus(value: string | null | undefined): string {
  if (value === "YES") return "Stopped";
  if (value === "NO") return "Not Stopped";
  return "—";
}

function fmtName(a: AssigneeInfo): string {
  const parts = [a.first_name?.trim(), a.last_name?.trim()].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : a.username;
}

export function formatSessionClipboard(params: SessionClipboardParams): string {
  const {
    locationName, turbineSerialNumber, blades, caseNumber, deadline,
    ticketNumber, woAssignees,
    startedAt, endedAt,
    taskCheckin, damCheckin, dfCheckin, weatherCheckin, progressCheckin, carCheckin,
    taskCheckout, damCheckout, dfCheckout, weatherCheckout, progressCheckout, turbineStoppedCheckout, carCheckout,
    selectedBlades,
    phase = "full",
  } = params;

  // Identical in both blocks, and the dedup only has to run once.
  const bladeLines: string[] = [];
  if (selectedBlades !== undefined) {
    // Field app: show only the blades the session was checked in against.
    if (selectedBlades === null || selectedBlades.length === 0) {
      bladeLines.push("Blade: N/A");
    } else {
      pushNumberedBladeLines(bladeLines, selectedBlades);
    }
  } else if (blades.length === 0) {
    bladeLines.push("Blade: N/A");
  } else {
    pushNumberedBladeLines(bladeLines, blades);
  }

  const lines: string[] = [];

  // Each phase is a self-contained block: the work-order context is repeated above
  // every phase's own values so that one block can be pasted on its own.
  const pushPhaseBlock = (marker: string, phaseValues: PhaseValues): void => {
    lines.push(marker);
    if (ticketNumber != null) lines.push(`Work Order: #${ticketNumber}`);
    lines.push(`Date/Time: ${phaseValues.dateTime}`);
    lines.push(`Site: ${f(locationName)}`);
    lines.push(`WTG: ${f(turbineSerialNumber)}`);
    lines.push(...bladeLines);
    if (phaseValues.df !== undefined) lines.push(`DF: ${f(phaseValues.df)}`);
    lines.push(`Task: ${f(phaseValues.task)}`);
    lines.push(`INC: ${f(caseNumber)}`);
    lines.push(`DAM: ${f(phaseValues.dam)}`);
    lines.push(`Weather: ${f(phaseValues.weather)}`);
    lines.push(`${phaseValues.progressLabel}: ${f(phaseValues.progress)}`);
    if (phaseValues.turbineStopped !== undefined)
      lines.push(`Turbine status: ${fmtTurbineStatus(phaseValues.turbineStopped)}`);
    if (phaseValues.estimatedCompletionDate !== undefined)
      lines.push(`Estimated Comp. Date: ${fmtDate(phaseValues.estimatedCompletionDate)}`);
    lines.push(`Vehicle: ${f(phaseValues.car)}`);
    lines.push(
      `Team: ${woAssignees.length > 0 ? woAssignees.map(fmtName).join(", ") : "—"}`
    );
  };

  const emitCheckin = phase !== "checkout";
  const emitCheckout = phase !== "checkin";

  if (emitCheckin) {
    pushPhaseBlock("--- Check In ---", {
      dateTime: fmtDateTime(startedAt),
      progressLabel: "Plan For Today",
      task: taskCheckin,
      dam: damCheckin,
      df: dfCheckin,
      weather: weatherCheckin,
      progress: progressCheckin,
      car: carCheckin,
    });
  }

  if (emitCheckout) {
    pushPhaseBlock("--- Check Out ---", {
      dateTime: endedAt ? fmtDateTime(endedAt) : "Not yet checked out",
      progressLabel: "Work Done Today",
      task: taskCheckout,
      dam: damCheckout,
      df: dfCheckout,
      weather: weatherCheckout,
      progress: progressCheckout,
      turbineStopped: turbineStoppedCheckout ?? null,
      estimatedCompletionDate: deadline ?? null,
      car: carCheckout,
    });
  }

  return lines.join("\n");
}
